// src/shared/components/layout/AuthenticatedLayout.tsx
import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { ThemeProvider } from '../../../core/providers/ThemeProvider';
import { Footer } from './Footer/Footer';
import { useUserStore } from '../../../app/store/useUserStore';
import { isSenderRole, isTravellerRole } from '../../utils/roleUtils';
import { ClipLoader } from 'react-spinners';
import { HeroSection } from '../sections/hero/HeroSection';
import { FloatingActionButton } from '../ui/FloatingActionButton/FloatingActionButton';

import { useAuth0 } from '@auth0/auth0-react';
import { createMuiTheme } from '../../styles/theme/theme';

interface AuthenticatedLayoutProps {
    children: React.ReactNode;
    showHeader?: boolean;
    showFooter?: boolean;
    showFAB?: boolean;
}

export const AuthenticatedLayout: React.FC<AuthenticatedLayoutProps> = ({
    children,
    showHeader = true,
    showFooter = true,
    showFAB = false
}) => {
    const { user, isProfileLoading } = useUserStore();
    const { isAuthenticated } = useAuth0();

    // Get user role from Zustand store (comes from backend)
    const userType = user?.user_role || 'traveller';

        // Determine if FAB should be shown - for both sender and traveller
        const shouldShowFAB =
            showFAB &&
            isAuthenticated &&
            (isSenderRole(user?.user_role) || isTravellerRole(user?.user_role));

        // Create MUI theme based on user role - MUST be called before any conditional returns
        const muiTheme = useMemo(() => createMuiTheme(userType), [userType]);

        // Show loading state while profile is being fetched
        if (isProfileLoading) {
            return (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        minHeight: '100vh',
                    }}
                >
                    <ClipLoader color="#9B1B5A" />
                </Box>
            );
        }

        // Ensure we have a valid user role
        if (!userType || (!isTravellerRole(userType) && !isSenderRole(userType))) {
            return (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        minHeight: '100vh',
                    }}
                >
                    <Typography>Invalid user role. Please contact support.</Typography>
                </Box>
            );
        }

    return (
        <MuiThemeProvider theme={muiTheme}>
            <ThemeProvider userType={userType}>
                <div className="min-h-screen flex flex-col" style={{ position: 'relative' }}>
                    {showHeader && (
                        <Box component="header" sx={{ position: 'relative' }}>
                            {/* Hero Section with integrated header */}
                            <HeroSection />
                        </Box>
                    )}

                    <main className="flex-1">{children}</main>

                    {showFooter && <Footer userType={userType} />}

                    {/* Floating Action Button */}
                    {shouldShowFAB && <FloatingActionButton />}
                </div>
            </ThemeProvider>
        </MuiThemeProvider>
    );
};

AuthenticatedLayout.displayName = 'AuthenticatedLayout';